import React, { useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const ImageLightbox = ({ open, src, alt, images, onClose }) => {
  // Normalize: if `images` is provided, use it; otherwise fallback to single src
  const initial = (images && images.length > 0 ? images : (src ? [src] : []));
  const [brokenSet, setBrokenSet] = useState(new Set());
  const list = initial.filter((u) => !brokenSet.has(u));
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (open) {
      setIndex(0);
      setBrokenSet(new Set());
    }
  }, [open, src, images]);

  useEffect(() => {
    // dacă s-a marcat current ca broken, mută indexul în limite
    if (index >= list.length && list.length > 0) setIndex(0);
  }, [list.length, index]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + list.length) % Math.max(1, list.length));
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % Math.max(1, list.length));
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handler);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handler);
    };
  }, [open, onClose, list.length]);

  const markBroken = (url) => {
    setBrokenSet((prev) => {
      if (prev.has(url)) return prev;
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  };

  if (!open || list.length === 0) return null;

  const current = list[index];
  const showNav = list.length > 1;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/90 p-4 md:p-10 cursor-zoom-out animate-in fade-in duration-200"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors backdrop-blur-sm z-10"
        aria-label="Închide"
      >
        <X className="w-6 h-6" />
      </button>

      {showNav && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setIndex((i) => (i - 1 + list.length) % list.length); }}
            className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors backdrop-blur-sm z-10"
            aria-label="Imaginea precedentă"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setIndex((i) => (i + 1) % list.length); }}
            className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors backdrop-blur-sm z-10"
            aria-label="Imaginea următoare"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      <img
        src={current}
        alt={alt || ''}
        onClick={(e) => e.stopPropagation()}
        onError={() => markBroken(current)}
        className="max-w-full max-h-full object-contain rounded-sm shadow-2xl cursor-default select-none"
      />

      {/* Counter + caption */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 max-w-[90%] flex items-center gap-3 text-white/90 text-sm bg-black/40 backdrop-blur-sm px-4 py-2 rounded-sm">
        {showNav && <span className="text-white/60 font-medium">{index + 1} / {list.length}</span>}
        {alt && <span className="text-white/80 line-clamp-1">{alt}</span>}
      </div>

      {/* Thumbnails strip (only if more than 1 image) */}
      {showNav && list.length <= 6 && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2 max-w-[95%] overflow-x-auto">
          {list.map((thumb, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setIndex(i); }}
              className={`w-14 h-14 md:w-16 md:h-16 rounded-sm overflow-hidden flex-shrink-0 border-2 transition-all ${
                i === index ? 'border-amber-500 opacity-100' : 'border-white/30 opacity-60 hover:opacity-100'
              }`}
              aria-label={`Imaginea ${i + 1}`}
            >
              <img src={thumb} alt="" onError={() => markBroken(thumb)} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageLightbox;
